import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { Search } from '../component/filter'
import { TableAG } from './table-ag'
import { ColumnPropsProvider, getColumns } from './table-columns'
import { useSelectColumn } from './grid-selection'
import { useGetQueryValues, useSetQueryValues } from './hooks'
import { QueryItems } from './component/table-query-items'
import { useTableToolbar } from './table-toolbar'
import { useLiveRefresh } from './use-live-refresh'
import { LiveIndicator } from './component/live-indicator'

// `queryKey` namespaces this grid's filter/sort/scroll query-string keys -- the
// same optional prop TableAgClient takes, sharing the same hooks. Omitting it
// keeps the historical unprefixed keys.
//
// `emptyState` / `noMatchState`: the grid's zero-state copy, as on TableAgClient.
// `pinFirstColumn`: as on TableAgClient.
// hideSearch: no search box, for a grid whose onGetRows doesn't search (a
// server that pages by cursor with filters of its own above the grid).
// reloadKey: a page whose onGetRows asks for something else when one of its
// controls changes (a filter) changes this, and the grid drops its rows and
// asks again from the first page, in place. The control, which may sit in this
// grid's own toolbar (TableToolbarContext), is not remounted, so it keeps its
// focus. refreshKey: a page that changed a row (a saved mark) changes this, and
// the grid asks again for the blocks it holds (refreshInfiniteCache), in place:
// the scroll position stays and no overlay shows. The page's onGetRows keeps
// whatever it remembers (a cursor pager its block cursors) for this. Omitting
// it changes nothing. The datasource keeps the onGetRows the grid was ready with, so that
// function must read the control's latest value (a ref), not close over it.
// selection: the page's select mode (grid-selection.js useGridSelection).
// While it is selecting the grid shows its checkbox column, pinned left, and
// a row click ticks the row; rows not loaded yet are in "all matching". A
// new search, a cleared search and a reloadKey change clear it: what was
// selected was another list's.
export const TableAgInfinite = forwardRef(({ children, onGetRows, queryKey, onRowClicked, onDestoryed, pollInterval, sentinelServer, sentinelEndpoint, defaultSort, emptyState, noMatchState, getRowClass, rowHeight, pinFirstColumn, hideSearch, reloadKey, refreshKey, selection }, ref) => {
  const tableRef = useRef()
  const toolbar = useTableToolbar()

  const { health, lastUpdatedAt } = useLiveRefresh({ gridRef: tableRef, pollInterval, sentinelServer, sentinelEndpoint })

  const { columnChildren, clearSelection } = useSelectColumn(children, selection)
  let columns = useMemo(() => getColumns(columnChildren), [columnChildren])

  const [search, updateSearch] = useState('')
  // The search the server last applied (search above is the live input box).
  const [appliedSearch, updateAppliedSearch] = useState('')
  const [loading, updateLoading] = useState(true)
  // Bumped by every full reload (search, refresh, clear, reloadKey). An answer
  // to an older one is dropped: it neither ends the loading overlay nor
  // scrolls, so only the newest search is shown (search as you type, owner
  // 2026-10-21; AG Grid already ignores rows for blocks a purge destroyed).
  const generation = useRef(0)

  const getQueryValues = useGetQueryValues(queryKey)
  const setQueryValues = useSetQueryValues(queryKey)

  const handleReady = async agGrid => {
    let datasource = {
      getRows: async params => {
        let sortCol = ''
        let sortDir = ''

        if (params.sortModel.length) {
          sortCol = params.sortModel[0].colId
          sortDir = params.sortModel[0].sort
        }

        const queryValues = getQueryValues()

        updateAppliedSearch(queryValues.search || '')

        setQueryValues(undefined, undefined, sortCol, sortDir)

        if (onGetRows) {
          // Only ever turn the blocking overlay OFF here. It is turned ON solely by
          // user-initiated full reloads (search / reload / clear) and the initial
          // load. Live-refresh (refreshInfiniteCache) and scroll-to-next-block
          // therefore update passively — no overlay, table stays interactive.
          const asked = generation.current
          let result = await onGetRows(params.startRow, params.endRow, queryValues.search, sortCol, sortDir)

          if (asked !== generation.current) return

          if (result) {
            params.successCallback(result.data, result.count)
          }
          
          updateLoading(false)

          if (queryValues.index !== null) {
            tableRef.current?.api?.ensureIndexVisible(queryValues.index, 'top')
          }
        }
      }
    }

    const queryValues = getQueryValues()

    if (queryValues.search) {
      updateSearch(queryValues.search)
    }

    if (queryValues.sortCol && queryValues.sortDir) {
      tableRef.current?.api?.applyColumnState({
        state: [
          {
            colId: queryValues.sortCol,
            sort: queryValues.sortDir
          }
        ]
      })
    } else if (defaultSort) {
      tableRef.current?.api?.applyColumnState({
        state: [
          {
            colId: defaultSort.colId,
            sort: defaultSort.sort
          }
        ]
      })
    }

    agGrid.api.setGridOption('datasource', datasource)
  }

  const handleSearch = search => {
    setQueryValues(search, 0, undefined, undefined)
    clearSelection()

    generation.current++
    updateLoading(true)
    tableRef.current?.api?.purgeInfiniteCache()
    tableRef.current?.api?.paginationGoToPage(0)
  }

  const handleRefresh = () => {
    generation.current++
    updateLoading(true)
    tableRef.current?.api?.purgeInfiniteCache()
    tableRef.current?.api?.paginationGoToPage(0)
  }

  const lastReloadKey = useRef(reloadKey)
  useEffect(() => {
    if (lastReloadKey.current === reloadKey) return
    lastReloadKey.current = reloadKey
    // The new rows are another report's: they start at the top, not where the
    // old ones were scrolled to (the datasource scrolls to the stored index
    // when it is asked again). Passing the search along makes this a
    // write-through; a scroll-only write is held 150 ms, and the datasource's
    // own sort write would drop it.
    setQueryValues(getQueryValues().search, 0, undefined, undefined)
    clearSelection()
    handleRefresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  const lastRefreshKey = useRef(refreshKey)
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return
    lastRefreshKey.current = refreshKey
    tableRef.current?.api?.refreshInfiniteCache()
  }, [refreshKey])

  const handleClearState = () => tableRef.current?.api?.resetColumnState()

  const handleClearSearch = () => {
    updateSearch('')
    setQueryValues('', 0, undefined, undefined)
    clearSelection()
    generation.current++
    updateLoading(true)
    tableRef.current?.api?.purgeInfiniteCache()
    tableRef.current?.api?.paginationGoToPage(0)
  }

  const handleScrollVertical = index => setQueryValues(undefined, index, undefined, undefined)

  return (
    <div className="d-flex flex-column h-100">
      <div>
        <QueryItems queryKey={queryKey} columns={columns} onClearSort={handleClearState} onClearSearch={handleClearSearch} />
      </div>
      <div className={`d-flex align-items-center gap-2${toolbar.start || toolbar.end ? ' tb-toolbar' : ''}`} data-testid="table-toolbar">
        {toolbar.start ? <div className="tb-toolbar-start">{toolbar.start}</div> : null}
        <div className={toolbar.start || toolbar.end ? 'tb-toolbar-filter' : 'flex-fill'}>
          {!hideSearch && <Search value={search} onChange={updateSearch} onSearch={handleSearch}  />}
        </div>
        {pollInterval ? <LiveIndicator health={health} lastUpdatedAt={lastUpdatedAt} /> : null}
        {toolbar.end ? <div className="tb-toolbar-end">{toolbar.end}</div> : null}
      </div>
      <div className="flex-fill">
        {/* the cells of the page's selector, menu and link columns read its latest props (table-columns.js) */}
        <ColumnPropsProvider columns={columnChildren}>
          <TableAG
            ref={tableRef}
            funcRef={ref}
            columns={columns}
            loading={loading}
            emptyState={emptyState}
            noMatchState={noMatchState}
            searchActive={!!appliedSearch}
            onClearSearch={handleClearSearch}
            rowModelType="infinite"
            // cacheBlockSize={1000}
            cacheBlockSize={200}
            maxBlocksInCache={10}
            cacheOverflowSize={10}
            infiniteInitialRowCount={10}
            onReady={handleReady}
            onRefresh={handleRefresh}
            onRowClicked={onRowClicked}
            getRowClass={getRowClass}
            rowHeight={rowHeight}
            pinFirstColumn={pinFirstColumn}
            selection={selection}
            onScrollVertical={handleScrollVertical}
            onDestoryed={onDestoryed}
          />
        </ColumnPropsProvider>
      </div>
    </div>
  )
})
